import {
    VuexModule, Module, Mutation, getModule, Action
} from 'vuex-module-decorators'
import store from '@/store'
import { msalAuth } from '@/services/MsalService';
import { CurrentUser } from '@/model/currentuser/CurrentUser';
import axios from 'axios'
import { RouteLocationNormalizedLoaded } from 'vue-router';
import { ExternalAudiences } from '../../../configs/MsalConfig';

export interface IAzureB2C {
    token: null | string
}

/* eslint-disable camelcase */
export interface TokenData {
    display_name: string
    name: string
    oid: string // objectID
    email: string
    groups: string[]
    roles: string[]
    aud: string
    upn: string
    preferred_username: string
    exp: number // expiration 
}
/* eslint-enable camelcase */

export async function grabToken(): Promise<string> {
    const token = await msalAuth.token(false)
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    AzureIdentificationModule.setToken(token)
    console.log(AzureIdentificationModule.expirationTime)
    if (AzureIdentificationModule.isInRole('Portal-Customer') && AzureIdentificationModule.isTokenValid) {
        await getAzureStorageData()
    }
    return token ?? ''
}

export async function grabTokenOverride(): Promise<string> {
    const token = await msalAuth.token(true)
    console.log('grabTokenOverride method')
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    AzureIdentificationModule.setToken(token)
    console.log(AzureIdentificationModule.expirationTime)
    if (AzureIdentificationModule.isInRole('Portal-Customer') && AzureIdentificationModule.isTokenValid) {
        await getAzureStorageData()
    }
    return token ?? ''
}

export async function getAzureStorageData(): Promise<void> {
    try {
        axios.get('api/ResourceHandler/SAS').then(response => {
            const sasURI = response.data
            const urlParts = sasURI.split('?');
            //console.log(urlParts)
            AzureIdentificationModule.setResourcesToken(urlParts[1])
        }).catch(error => console.log(error))
    } catch (err) {
        //console.log(err);
        throw err;
    }
}

@Module({
    dynamic: true, store, namespaced: true, name: 'azureIdentification'
})
class AzureIdentification extends VuexModule implements IAzureB2C {
    private tokenHolder: string | null = null

    public errorCode: string | null = null

    public currentUser: CurrentUser | null = null

    public resourcesToken: string | null = null

    private get tokenData(): TokenData | null {
        if (this.tokenHolder) {
            const tokenData = JSON.parse(atob(this.tokenHolder.split('.')[1])) as TokenData
            return tokenData
        }
        return null
    }

    get authenticated(): boolean {
        //console.log('Internal:', msalAuth.workforceAccount.value)
        //console.log('External:', msalAuth.externalAccount.value)
        return msalAuth.workforceAccount.value != null || msalAuth.externalAccount.value != null
    }

    get isExternalAccount(): boolean {
        if (this.tokenData) {
            return ExternalAudiences.indexOf(this.tokenData.aud) > -1
        }
        return false
    }

    get displayName(): string | null {
        if (this.tokenData) {
            return this.tokenData.name ?? this.tokenData.display_name
        }
        return null
    }

    get hasTokenData(): boolean {
        return this.tokenData != null
    }

    get portalID(): string | null {
        if (this.tokenData) {
            return this.tokenData.oid
        }
        return null
    }

    get portalEmail(): string | null {
        if (this.tokenData) {
            return this.tokenData.upn ?? this.tokenData.preferred_username
        }
        return null
    }

    get isNewUser(): boolean {
        if (this.tokenData) {
            return !this.isInRole('Portal-Customer')
        }
        return false
    }

    get azureGroups(): string[] | null {
        if (this.tokenData) {
            return this.tokenData.groups
        }
        return null
    }
    get azureRoles(): string[] | null {
        if (this.tokenData) {
            return this.tokenData.roles
        }
        return null
    }

    get allRoles(): string[] | null {
        if (this.azureGroups != undefined && this.azureRoles != undefined) {
            return this.azureGroups.concat(this.azureRoles)
        } else if (this.azureRoles != undefined) {
            return this.azureRoles
        } else if (this.azureGroups != undefined) {
            return this.azureGroups
        } else {
            return null
        }
    }

    get roles(): string[] | null {
        let allRoles: string[] = []
        if (this.allRoles) {
            allRoles = this.allRoles
        }
        if (this.currentUser) {
            allRoles = allRoles.concat(this.currentUser.roles)
            let customRole = ''
            if (this.currentUser.isInternalUser) {
                customRole = 'Internal'
            } else if (this.currentUser.isRsm) {
                customRole = 'RSM'
            } else {
                customRole = 'Customer'
            }
            allRoles = allRoles.concat([customRole])
        }
        allRoles = [...new Set([...allRoles])]
        return allRoles
    }

    get isInRole() {
        return (role: string | string[]) => {
            if (role && this.roles) {
                if (typeof role == 'string') {
                    return this.roles.some((identityRole: string) => role.toLowerCase() === identityRole.toLowerCase())
                } else {
                    return role.map((singleRole: string) => this.roles!.some((identityRole: string) => singleRole.toLowerCase() === identityRole.toLowerCase()))
                        .reduce((previousValue: boolean, currentValue: boolean) => previousValue || currentValue)
                }
            }
            return false
        }
    }

    get token(): string | null {
        return this.tokenHolder
    }

    get error(): string | null {
        return this.errorCode
    }

    get isTokenValid(): boolean {
        let isValid = false
        if (this.tokenData) {
            const seconds = this.tokenData.exp
            const expDate = new Date(0)
            expDate.setUTCSeconds(seconds)
            // set to 5 minutes before to start grabbing refresh token
            expDate.setUTCMinutes(expDate.getUTCMinutes() - 5) 
            const current = Date.now()
            //console.log(current)
            //console.log(expDate.getTime())
            isValid = current <= expDate.getTime()
        }
        return isValid
    }

    /**
     * Grabs the token's expiration datetime
     */
    get expirationTime(): Date | null {
        let time = null
        if (this.tokenData) {
            const seconds = this.tokenData.exp
            const expDate = new Date(0)
            expDate.setUTCSeconds(seconds)
            time = expDate
        }
        return time
    }

    @Mutation
    public setToken(token: string | null) {
        this.tokenHolder = token
    }

    @Mutation
    setError(code: string | null) {
        this.errorCode = code
    }

    @Mutation
    public setCurrentUser(user: CurrentUser) {
        this.currentUser = user
    }

    @Mutation
    public setResourcesToken(token: string): void {
        this.resourcesToken = token
    }

    get isInternal(): boolean {
        if (this.currentUser) {
            return this.currentUser.isInternalUser || this.currentUser.isRsm
        } else {
            return false
        }
    }

    get isApprover(): boolean {
        if (this.currentUser) {
            return this.isInRole(['InsideSalesDirector', 'VicePresidentSales', 'President', 'IT'])
        }
        return false
    }
}

export const AzureIdentificationModule = getModule(AzureIdentification)
